import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useCurrencyStore, { formatPrice } from '../store/useCurrencyStore';
import api from '../services/api';

const STATUS_COLORS = {
  PENDING:   'bg-yellow-500/20 text-yellow-400',
  ACTIVE:    'bg-blue-500/20 text-blue-400',
  COMPLETED: 'bg-green-500/20 text-green-400',
  CANCELLED: 'bg-red-500/20 text-red-400',
  DISPUTED:  'bg-orange-500/20 text-orange-400',
};

function Sparkline({ data, color = '#6C5CE7' }) {
  if (!data?.length) return null;
  const max = Math.max(...data.map(d => d.net), 1);
  const pts = data.map((d, i) => {
    const x = (i / (data.length - 1)) * 280;
    const y = 52 - (d.net / max) * 44;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width="280" height="56" viewBox="0 0 280 56" className="w-full">
      <polyline fill={`${color}22`} stroke="none" points={`0,56 ${pts} 280,56`} />
      <polyline fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" points={pts} />
    </svg>
  );
}

export default function EarningsPage() {
  const navigate = useNavigate();
  const { currency } = useCurrencyStore();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/earnings')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const s = data?.summary;

  return (
    <div className="min-h-screen bg-dark-bg pb-24">
      {/* Header */}
      <div className="px-4 pt-12 pb-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-dark-subtext">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <h1 className="text-xl font-bold text-dark-text" style={{ letterSpacing: '-0.5px' }}>My Earnings</h1>
      </div>

      {loading ? (
        <div className="px-4 space-y-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-dark-surface rounded-2xl animate-pulse" />)}
        </div>
      ) : (
        <div className="px-4 space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-dark-surface border border-dark-border rounded-2xl p-4 col-span-2"
              style={{ background: 'linear-gradient(135deg, rgba(108,92,231,0.15), rgba(108,92,231,0.05))' }}>
              <p className="text-dark-subtext text-xs mb-1">Total Earned (after commission)</p>
              <p className="text-3xl font-bold text-dark-text">{formatPrice(s?.totalNet || 0, currency)}</p>
              <p className="text-dark-subtext text-xs mt-1">{s?.completedOrders || 0} completed orders · {Math.round((s?.commissionRate || 0.15) * 100)}% platform fee</p>
            </div>
            <div className="bg-dark-surface border border-dark-border rounded-2xl p-4">
              <p className="text-dark-subtext text-xs mb-1">This Month</p>
              <p className="text-xl font-bold text-green-400">{formatPrice(s?.monthNet || 0, currency)}</p>
            </div>
            <div className="bg-dark-surface border border-dark-border rounded-2xl p-4">
              <p className="text-dark-subtext text-xs mb-1">Pending Orders</p>
              <p className="text-xl font-bold text-yellow-400">{s?.pendingOrders || 0}</p>
            </div>
          </div>

          {/* Monthly chart */}
          {data?.monthly?.length > 0 && (
            <div className="bg-dark-surface border border-dark-border rounded-2xl p-4">
              <p className="text-sm font-semibold mb-3">Last 6 Months</p>
              <Sparkline data={data.monthly} color="#00b894" />
              <div className="flex justify-between mt-2">
                {data.monthly.map(m => (
                  <div key={m.label} className="text-center">
                    <p className="text-[9px] text-dark-subtext">{m.label}</p>
                    <p className="text-[10px] font-medium text-dark-text">{formatPrice(m.net, currency).split(' ')[0]}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top services */}
          {data?.topServices?.length > 0 && (
            <div className="bg-dark-surface border border-dark-border rounded-2xl p-4">
              <p className="text-sm font-semibold mb-3">Top Earning Services</p>
              <div className="space-y-3">
                {data.topServices.map((svc, i) => (
                  <div key={svc.serviceId} className="flex items-center gap-3">
                    <span className="text-dark-subtext text-xs w-4">{i + 1}</span>
                    {svc.image ? (
                      <img src={svc.image} alt={svc.title} className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-dark-border flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-dark-text truncate">{svc.title}</p>
                      <p className="text-xs text-dark-subtext">{svc.bookings} orders</p>
                    </div>
                    <p className="text-sm font-bold text-green-400 flex-shrink-0">{formatPrice(svc.net, currency)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent orders */}
          {data?.recentOrders?.length > 0 && (
            <div>
              <p className="text-sm font-semibold mb-3">Recent Orders</p>
              <div className="space-y-2">
                {data.recentOrders.map(b => (
                  <div key={b.id} className="bg-dark-surface border border-dark-border rounded-2xl p-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-dark-border overflow-hidden flex-shrink-0">
                      {b.service?.images?.[0] && <img src={b.service.images[0]} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-dark-text truncate">{b.service?.title}</p>
                      <p className="text-xs text-dark-subtext">{b.client?.fullName}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-dark-text">{formatPrice(b.totalAmount * (1 - (s?.commissionRate || 0.15)), currency)}</p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${STATUS_COLORS[b.status]}`}>{b.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!data?.recentOrders?.length && !loading && (
            <div className="text-center py-16">
              <p className="text-4xl mb-3">💰</p>
              <p className="text-dark-text font-semibold">No earnings yet</p>
              <p className="text-dark-subtext text-sm mt-1">Complete your first order to see earnings here</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
